import React, { createContext, useState, useContext, useEffect } from "react";
import { getPosts, PostWithItems } from "../../services/PostServices";
import { useAuth } from "../AuthProvider";

interface PostContextProps {
  posts: PostWithItems[];
  setPosts: React.Dispatch<React.SetStateAction<PostWithItems[]>>;
  postsPage: number;
  setPostsPage: React.Dispatch<React.SetStateAction<number>>;
  hasMorePosts: boolean;
  setHasMorePosts: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const CreatorPostContext = createContext<PostContextProps | undefined>(
  undefined
);

export const CreatorPostProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [posts, setPosts] = useState<PostWithItems[]>([]);
  const [postsPage, setPostsPage] = useState<number>(0);
  const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const limit = 8;

  useEffect(() => {
    const fetchPosts = async () => {
      if (loading || !hasMorePosts) return;

      setLoading(true);
      setError(null);

      try {
        const offset = (postsPage - 1) * limit;
        const fetchedPosts = await getPosts(limit, offset);

        if (fetchedPosts.length < limit) {
          setHasMorePosts(false);
        }

        setPosts((prevPosts) => {
          const newPosts = fetchedPosts.filter(
            (fetchedPost) =>
              !prevPosts.some((prevPost) => prevPost.id === fetchedPost.id)
          );
          return [...prevPosts, ...newPosts];
        });
      } catch (err) {
        setError("Failed to fetch posts");
      } finally {
        setLoading(false);
      }
    };

    if (user && postsPage > 0) fetchPosts();
    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsPage]);

  useEffect(() => {
    setPostsPage(0);
    setHasMorePosts(true);
    setPosts([]);
  }, [user]);

  return (
    <CreatorPostContext.Provider
      value={{
        posts,
        setPosts,
        postsPage,
        setPostsPage,
        hasMorePosts,
        setHasMorePosts,
        loading,
        setLoading,
        error,
        setError,
      }}
    >
      {children}
    </CreatorPostContext.Provider>
  );
};

export const useCreatorPostsContext = () => {
  const context = useContext(CreatorPostContext);
  if (!context) {
    throw new Error("usePostContext must be used within a CreatorPostProvider");
  }
  return context;
};
